/**
 * 칸반보드 — 이 도구의 첫 화면.
 *
 * **뷰가 셋이다** (노션): 보드 · 표 · 목록. 셋이 **같은 목록**을 다르게
 * 그린다 — 서버는 평평한 배열 하나를 주고 묶는 것은 여기 몫이다.
 *
 * **필터는 서버가 건다** (Jira): 담당자·타입·우선순위·라벨. 카드를 전부
 * 받아 화면에서 거르면 프로젝트가 커질 때 무너지고, 「걸렀는데 개수 배지는
 * 전체」 같은 어긋남이 생긴다.
 */

import { toast } from 'sonner';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  createTicket,
  draftReady,
  errorMessage,
  getTicketMeta,
  listLabels,
  listMembers,
  listTickets,
  can,
  moveTicket,
  NotLoggedInError,
  type Label,
  type Project,
  type TemplateSection,
  type TicketQuestion,
  type TicketCard,
  type TicketCol,
  type TicketFilter,
  type TicketKind,
  type User,
  type UserBrief,
} from './api';
import { BoardToolbar, type BoardView } from './board/BoardToolbar';
import { CreateChat } from './board/CreateChat';
import { CreateStart } from './board/CreateStart';
import { CreateForm, type CreateInput, type DraftSeed } from './board/CreateForm';
import { BoardSummary } from './board/BoardSummary';
import { TicketCardView } from './board/TicketCardView';
import { TicketListView } from './board/TicketListView';
import { TicketTableView } from './board/TicketTableView';
import { COLUMNS, columnLabel, EMPTY_COLUMN } from './columns';
import { TicketDialog } from './ticket/TicketDialog';
import { Modal } from './ui/Modal';

const DRAG_TYPE = 'application/x-office-ticket';

type Props = {
  user: User;
  projectId: number | null;
  /** 고를 수 있는 프로젝트. **도구줄이 이것으로 선택기를 그린다**(2026-08-26). */
  projects: Project[];
  onProjectChange: (id: number) => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
};

/**
 * 필터가 하나라도 걸렸나. 걸렸으면 **순서를 못 정한다** — 안 보이는 카드가
 * 사이에 있어 이웃 기준 자리 계산이 어긋난다(아래 `handleMove` 참고).
 *
 * ⚠ **`TicketFilter` 에 축을 더하면 여기도 더해라.** 빠뜨리면 그 축으로
 * 거른 채 드래그할 때 엉뚱한 자리에 꽂힌다 — 「내가 낸 것」(`reporter_id`)
 * 을 더할 때(2026-09-01) 실제로 빠뜨리기 쉬운 자리라 함수로 빼고 시험을
 * 걸어 뒀다(`test/board-filter.test.tsx`).
 */
export function hasActiveFilter(filter: TicketFilter): boolean {
  return (
    filter.assignee_id != null ||
    filter.reporter_id != null ||
    filter.type != null ||
    filter.priority != null ||
    filter.label_id != null ||
    filter.kind != null ||
    filter.device != null ||
    filter.environment != null ||
    // ⚠ **`!= null` 이어야 한다.** 보류는 축이 셋(전부·숨김·만)이고
    // `false`(= 「미룬 것 빼고」)도 사람이 고른 값이다. `filter.held &&`
    // 로 쓰면 가장 흔한 그 걸음에서 이 함수가 거짓을 답해, **칩 줄이 안
    // 뜨고** 「필터 중에는 순서를 못 정한다」 안내도 사라진다 — 그러면
    // 안 보이는 카드를 기준으로 자리를 계산하는 자리로 그대로 걸어간다.
    filter.held != null ||
    (filter.q ?? '') !== ''
  );
}

/** 칸별로 묶는다. 서버가 평평하게 주므로 보드 뷰만 이것이 필요하다. */
function groupByColumn(tickets: TicketCard[]): Record<TicketCol, TicketCard[]> {
  const out = { issue: [], todo: [], in_progress: [], done: [] } as Record<
    TicketCol,
    TicketCard[]
  >;
  for (const ticket of tickets) out[ticket.col]?.push(ticket);
  return out;
}

/**
 * 낙관적으로 카드를 옮긴다. 서버가 거절하면 부르는 쪽이 되돌린다.
 *
 * **자리 값을 흉내내지 않는다** — 배열 순서만 바꾼다. 서버가 계산한
 * `position` 을 화면이 추측하면 다음 새로고침에 순서가 바뀐다.
 */
function placeLocally(
  tickets: TicketCard[],
  moved: TicketCard,
  col: TicketCol,
  beforeId: number | undefined,
): TicketCard[] {
  const rest = tickets.filter((item) => item.id !== moved.id);
  const next = { ...moved, col };
  if (beforeId === undefined) {
    // 칸 맨 끝. 그 칸 마지막 카드 뒤에 꽂는다.
    const lastAt = rest.map((item) => item.col).lastIndexOf(col);
    if (lastAt < 0) return [...rest, next];
    return [...rest.slice(0, lastAt + 1), next, ...rest.slice(lastAt + 1)];
  }
  const at = rest.findIndex((item) => item.id === beforeId);
  if (at < 0) return [...rest, next];
  return [...rest.slice(0, at), next, ...rest.slice(at)];
}

export function BoardPage({
  user,
  projectId,
  projects,
  onProjectChange,
  onError,
  onUnauthorized,
}: Props) {
  // 휴대폰에서는 모든 상태의 업무를 세로로 훑는다. 보드·표 전환은 그대로 둔다.
  const [view, setView] = useState<BoardView>(() =>
    window.matchMedia?.('(max-width: 960px)').matches ? 'list' : 'board',
  );
  /**
   * 휴대폰 폭인가(2026-10-19). 보드가 칸 넷을 가로로 이어 붙여(390px 화면에서
   * 1265px) 한 칸씩 옆으로 밀어야 했다 — 이 폭에서는 칸 하나만 보이고 위의
   * 탭으로 고른다.
   */
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.('(max-width: 720px)');
    if (!mq) return;
    const apply = () => setNarrow(mq.matches);
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  const [mobileCol, setMobileCol] = useState<TicketCol>('issue');
  /** 모달로 열어 둔 티켓. `null` 이면 안 열려 있다. */
  const [openId, setOpenId] = useState<number | null>(null);
  /**
   * 주소로 온 티켓(`/tickets/:id` — 남이 보낸 링크). **여는 길이 둘이다** —
   * 카드를 눌러 열 때는 주소를 안 바꾼다: 주소가 바뀌면 라우트가 갈려
   * 보드가 다시 그려지고, 스크롤·필터가 처음 자리로 간다 — 모달을 쓰는
   * 이유가 사라진다. 주소는 링크로 들어올 때만 쓰인다.
   *
   * 위키(`DocumentsPage`)처럼 주소에서 **바로 읽는다** — 상태로 옮겨 담고
   * 효과로 맞추면 모달이 한 박자 늦게 따라와 깜빡인다.
   */
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const routeId = useMemo(() => {
    const parsed = Number(idParam);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [idParam]);
  const [tickets, setTickets] = useState<TicketCard[] | null>(null);
  const [loadedProject, setLoadedProject] = useState<number | null>(null);
  const [counts, setCounts] = useState<Record<TicketCol, number> | null>(null);
  const [members, setMembers] = useState<UserBrief[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [filter, setFilter] = useState<TicketFilter>({});
  /** 만들기 모달이 열려 있나. **칸은 폼이 정한다**(2026-08-29). */
  const [creating, setCreating] = useState(false);
  /**
   * 모달 안이 어느 걸음인가 — **고르기 → 대화 → 폼**(2026-09-16 사용자 지정).
   *
   * 기본은 `start` 다. 다만 서버가 챗봇을 못 열면(`draft-ready` 거짓) 바로
   * `form` 이다 — 눌러도 503 이 나는 입구를 기본으로 두면 접수창구가 막힌
   * 것처럼 보인다.
   */
  const [createMode, setCreateMode] = useState<'start' | 'chat' | 'form'>('form');
  /** 첫 걸음에서 고른 것. 대화와 폼이 이 값을 그대로 쓴다. */
  const [pick, setPick] = useState<{ projectId: number; kind: TicketKind } | null>(null);
  /** 챗봇이 세운 초안. 폼의 시작값이다. `null` 이면 빈 폼. */
  const [draft, setDraft] = useState<{ seed: DraftSeed; evidence: string[]; model: string } | null>(null);
  /** 챗봇을 열 수 있나. `null` 은 아직 안 물어봤다. */
  const [chatReady, setChatReady] = useState<boolean | null>(null);
  /** 본문 양식. 서버가 준다 — 챗봇의 초안과 「양식 넣기」가 같은 절을 쓴다. */
  const [templates, setTemplates] = useState<Record<TicketKind, TemplateSection[]> | null>(null);
  /** 챗봇 고정 질문. 양식과 함께 한 번만 받는다. */
  const [questions, setQuestions] = useState<Record<TicketKind, TicketQuestion[]> | null>(null);
  /** 서버에 보내는 중. 단추를 두 번 누르지 못하게 막는다. */
  const [saving, setSaving] = useState(false);
  /* 화면이 무엇을 그릴지 정하는 값. **막는 것은 서버다**(`require_perm`) —
     여기서는 눌러도 403 이 나는 단추를 안 그릴 뿐이다. */
  const canCreate = can(user, 'ticket.create');
  const canMove = can(user, 'ticket.move');
  const [overCol, setOverCol] = useState<TicketCol | null>(null);
  // 드래그가 끝날 때 브라우저가 click 을 한 번 더 쏜다. 그것으로 티켓이
  // 열리면 「옮겼을 뿐인데 상세로 튄다」가 된다.
  const skipClick = useRef(false);
  const mutationVersion = useRef(0);
  const mutations = useRef(0);
  const dragging = useRef(false);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const filtered = hasActiveFilter(filter);

  // 양식과 챗봇 준비 여부는 프로젝트와 무관하다 — 한 번만 묻는다.
  // ⚠ **실패해도 보드는 뜬다.** 둘 다 없어도 폼은 그대로 돈다(양식 단추와
  // 챗봇 입구만 안 그린다). 여기서 `onError` 를 부르면 보드가 오류 띠부터
  // 보여 준다.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [meta, ready] = await Promise.allSettled([getTicketMeta(), draftReady()]);
      if (cancelled) return;
      if (meta.status === 'fulfilled') {
        setTemplates(meta.value.templates);
        setQuestions(meta.value.questions);
      }
      setChatReady(ready.status === 'fulfilled' && ready.value.ready);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (projectId === null) return;
    let cancelled = false;
    let fetching = false;
    async function read(background = false) {
      if (fetching || mutations.current || dragging.current || (background && document.hidden)) return;
      fetching = true;
      const version = mutationVersion.current;
      try {
        const [ticketData, labelData, memberData] = await Promise.all([
          listTickets(projectId as number, filter), listLabels(projectId as number), listMembers(),
        ]);
        if (cancelled || version !== mutationVersion.current || mutations.current || dragging.current) return;
        setTickets(ticketData.tickets); setCounts(ticketData.counts); setLoadedProject(projectId);
        setLabels(labelData.labels); setMembers(memberData.members); setBoardError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) { onUnauthorized(); return; }
        setBoardError(`보드를 불러오지 못했습니다. ${errorMessage(err)}`);
      } finally { fetching = false; }
    }
    const focus = () => { void read(true); };
    const startDrag = () => { dragging.current = true; };
    const endDrag = () => { dragging.current = false; };
    void read();
    const timer = window.setInterval(() => void read(true), 30_000);
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', focus);
    document.addEventListener('dragstart', startDrag);
    document.addEventListener('dragend', endDrag);
    document.addEventListener('drop', endDrag);
    return () => {
      cancelled = true; window.clearInterval(timer);
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', focus);
      document.removeEventListener('dragstart', startDrag);
      document.removeEventListener('dragend', endDrag);
      document.removeEventListener('drop', endDrag);
    };
  }, [projectId, filter, onUnauthorized, reloadKey]);

  const handleMove = useCallback(
    async (ticketId: number, col: TicketCol, beforeId?: number) => {
      if (!tickets) return;
      const current = tickets.find((item) => item.id === ticketId);
      if (!current) return;
      // **필터 중에는 자리를 안 정한다.** 화면에 안 보이는 카드가 사이에
      // 있어서, 보이는 이웃을 기준으로 계산하면 엉뚱한 자리에 꽂힌다.
      const target = filtered ? undefined : beforeId;
      if (target === ticketId) return;
      if (current.col === col && target === undefined) {
        const inCol = tickets.filter((item) => item.col === col);
        if (inCol[inCol.length - 1]?.id === ticketId) return;
      }

      mutations.current++; mutationVersion.current++;
      const snapshot = tickets;
      setTickets(placeLocally(tickets, current, col, target));
      onError(null);
      try {
        const saved = await moveTicket(ticketId, {
          col,
          ...(target !== undefined ? { before_id: target } : {}),
        });
        // 서버가 계산한 자리를 받아 둔다 — 다음 이동의 기준이 된다.
        setTickets((prev) =>
          prev ? prev.map((item) => (item.id === saved.id ? saved : item)) : prev,
        );
        if (current.col !== col) {
          setCounts((prev) =>
            prev
              ? { ...prev, [current.col]: prev[current.col] - 1, [col]: prev[col] + 1 }
              : prev,
          );
          // **칸이 바뀔 때만 알린다.** 같은 칸 안에서 순서를 바꾸는 것은
          // 눈앞에서 카드가 움직이므로 말이 필요 없다.
          toast.success(`${saved.key} → ${columnLabel(col)}`);
        }
      } catch (err) {
        setTickets(snapshot);
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        onError(`티켓 옮기기에 실패했다: ${errorMessage(err)}`);
        // 거절은 대개 **보드가 낡았다**는 뜻이다(남이 먼저 옮겨 이웃이 다른
        // 칸). 스냅숏만 되돌리면 같은 실패를 30초 폴링까지 반복한다.
        setReloadKey((key) => key + 1);
      } finally { mutations.current--; mutationVersion.current++; }
    },
    [tickets, filtered, onError, onUnauthorized],
  );

  function readDragId(event: DragEvent): number | null {
    const id = Number(event.dataTransfer.getData(DRAG_TYPE));
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  async function handleCreate(input: CreateInput) {
    if (projectId === null) return;
    setSaving(true);
    mutations.current++; mutationVersion.current++;
    onError(null);
    try {
      // **폼이 고른 프로젝트와 칸을 쓴다.** 지금 보고 있는 것이 기본값이지만,
      // 사람이 다른 데로 바꿨을 수 있다.
      const card = await createTicket(input);
      setTickets((prev) => (prev ? [...prev, card] : prev));
      setCounts((prev) =>
        prev ? { ...prev, [input.col]: prev[input.col] + 1 } : prev,
      );
      setCreating(false);
      // **번호를 알려 준다.** 만든 직후에 그것을 남에게 말할 일이 가장 많다.
      toast.success(`${card.key} 만들었다`);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`티켓을 만들지 못했다: ${errorMessage(err)}`);
    } finally {
      setSaving(false);
      mutations.current--; mutationVersion.current++;
    }
  }

  /**
   * 카드를 누르면 **모달**이다(2026-08-26 지시) — 페이지로 나가면 돌아올 때
   * 보드가 다시 그려져 스크롤과 필터가 처음 자리로 간다.
   */
  function openTicket(id: number) {
    if (skipClick.current) {
      skipClick.current = false;
      return;
    }
    setOpenId(id);
  }

  if (projectId === null) {
    return <p className="page-status">등록된 프로젝트가 없습니다.</p>;
  }
  if (tickets === null || loadedProject !== projectId) {
    return <div className="page-status">{boardError ? <><p role="alert">{boardError}</p><Button onClick={() => setReloadKey(key => key + 1)}>다시 시도</Button></> : '보드를 읽는 중…'}</div>;
  }

  const grouped = groupByColumn(tickets);

  return (
    <>
      {boardError && <p role="alert" className="text-meta text-st-red-f">{boardError} <Button size="sm" variant="outline" onClick={() => setReloadKey(key => key + 1)}>다시 시도</Button></p>}
      {/* ⚠ **「업무 현황」 배지 줄을 걷었다**(2026-09-16 사용자 지정).
          미배정·기한 초과·7일 이상 정체는 보드 위에 상시로 설 만큼 자주
          보는 수가 아니었고, 배포 대기는 **배포 화면에 그 칸이 이미 있다.**
          되살리려면 서버의 집계(`TicketList.summary`)와 `attention` 필터를
          함께 되살려야 한다 — 부르는 곳이 없어 그 둘도 같이 걷었다. */}
      <BoardToolbar
        projects={projects}
        projectId={projectId}
        onProjectChange={onProjectChange}
        view={view}
        onView={setView}
        filter={filter}
        /*
          ⚠ **여기서 `setTickets(null)` 을 하지 마라.** 그러면 아래
          「보드를 읽는 중…」 분기가 도구줄까지 통째로 걷어내고, 필터 팝오버가
          **고를 때마다 닫힌다**(실측). 축 여럿을 걸려면 매번 다시 열어야 했다.

          옛 목록을 잠시 두고 새 것이 오면 갈아 끼운다. 그 사이가 한 번의
          왕복이라 눈에 거의 안 띄고, 화면이 비었다가 다시 차는 깜빡임도
          사라진다.
        */
        onFilter={setFilter}
        members={members}
        labels={labels}
        me={user.id}
        filtered={filtered}
        canCreate={canCreate}
        summary={tickets ? <BoardSummary tickets={tickets} /> : null}
        onCreate={() => {
          setDraft(null);
          setPick(null);
          setCreateMode(chatReady ? 'start' : 'form');
          setCreating(true);
        }}
      />

      {view === 'board' && narrow && (
        <div className="col-tabs" role="tablist" aria-label="칸">
          {COLUMNS.map((def) => (
            <button
              key={def.id}
              type="button"
              role="tab"
              aria-selected={mobileCol === def.id}
              onClick={() => setMobileCol(def.id)}
              style={{ '--col-c': `var(--st-${def.tone}-f)` } as CSSProperties}
            >
              {def.label}
              <span className="tnum">{counts?.[def.id] ?? grouped[def.id].length}</span>
            </button>
          ))}
        </div>
      )}
      {view === 'board' && (
        <div className={narrow ? 'board is-single' : 'board'}>
          {COLUMNS.filter((def) => !narrow || def.id === mobileCol).map((def) => {
            const inCol = grouped[def.id];
            return (
              <div
                key={def.id}
                className={overCol === def.id ? 'column is-over' : 'column'}
                style={{ '--col-c': `var(--st-${def.tone}-f)` } as CSSProperties}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setOverCol(def.id);
                }}
                onDragLeave={(event) => {
                  const next = event.relatedTarget;
                  if (next instanceof Node && event.currentTarget.contains(next)) return;
                  setOverCol(null);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setOverCol(null);
                  const id = readDragId(event);
                  if (id !== null) void handleMove(id, def.id);
                }}
              >
                {/* 칸의 색은 **윗선 하나**가 진다(2026-10-18 C 현황판형).
                    머리를 통째로 칠하던 옛 판은 칸 넷이 카드보다 먼저
                    눈에 들어왔다. */}
                <div className="column-head">
                  <span className="column-name">{def.label}</span>
                  {/* **거른 뒤의 수다.** 서버가 세어 준 값을 그대로 쓴다 —
                      화면이 다시 세면 필터와 배지가 어긋난다. */}
                  <span className="column-count num">
                    {counts?.[def.id] ?? inCol.length}
                  </span>
                  {/* ⚠ **칸마다 있던 `+` 를 걷었다**(2026-08-29 지시:
                      「상태 이름 우측에 있는 것은 가독성이 떨어진다」).
                      칸 머리는 이름과 개수만 진다 — 만드는 자리는 도구줄의
                      단추 하나이고, 칸은 폼에서 고른다. */}
                </div>
                <div className="column-body">
                  {inCol.map((ticket) => (
                    <TicketCardView
                      key={ticket.id}
                      ticket={ticket}
                      draggable={canMove}
                      onOpen={() => openTicket(ticket.id)}
                      onDragStart={(event) => {
                        skipClick.current = false;
                        event.dataTransfer.setData(DRAG_TYPE, String(ticket.id));
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setOverCol(null);
                        skipClick.current = true;
                        const id = readDragId(event);
                        if (id !== null) void handleMove(id, def.id, ticket.id);
                      }}
                    />
                  ))}
                  {inCol.length === 0 && (
                    /*
                      빈 칸. **점선은 장식이 아니라 「여기에 놓을 수 있다」는
                      말이다** — 보드에서 빈 자리는 드롭 대상이고, 실선 상자로
                      두면 그냥 빈 카드처럼 보인다.

                      문구는 칸마다 다르다(`columns.ts`). 「비어 있다」 하나로
                      넷을 덮으면 그 자리에서 무엇을 해야 하는지 안 알려 준다.
                    */
                    <p className="column-empty">{EMPTY_COLUMN}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === 'table' && (
        <TicketTableView tickets={tickets} onOpen={openTicket} />
      )}
      {view === 'list' && <TicketListView tickets={tickets} onOpen={openTicket} />}

      {/*
        **새 티켓도 모달이다**(2026-08-26 지시). 칸 안 인라인 폼은 240px
        폭에서 「자세히」를 펴면 카드 여섯 장 높이가 됐다.
      */}
      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        /* **머리·본문·바닥 세 층으로 세운다**(2026-09-16) — 속성을 펴거나
           대화가 길어져도 제목과 동작 줄이 화면에 남는다. 세 걸음이 같은
           껍데기를 쓰므로 셋 다 그 구조를 지켜야 한다. */
        frame
        title="새 티켓"
        description={
          createMode === 'start'
            ? '프로젝트와 유형을 고르면 몇 가지를 묻는다. 직접 적을 수도 있다.'
            : createMode === 'chat'
              ? '답한 내용으로 PM 이 티켓 초안을 세운다.'
              : '제목만으로 만들 수 있다. 나머지는 「자세히」에 있다.'
        }
      >
        {/*
          **걸음 셋이 한 모달 안에서 이어진다**(2026-09-16 사용자 지정).
          고르기 → 대화 → 폼. 「직접 적기」는 어느 걸음에서든 폼으로 나가는
          문이고, 폼에서 「만들기」를 눌러야 티켓이 된다.
        */}
        {creating && projectId !== null && createMode === 'start' && (
          <CreateStart
            projects={projects}
            projectId={pick?.projectId ?? projectId}
            onProjectChange={(id) => setPick({ projectId: id, kind: 'issue' })}
            onPick={(kind) => {
              setPick({ projectId: pick?.projectId ?? projectId, kind });
              setCreateMode('chat');
            }}
            onSwitchToForm={() => setCreateMode('form')}
            onCancel={() => setCreating(false)}
          />
        )}
        {creating && pick !== null && createMode === 'chat' && questions !== null && (
          <CreateChat
            projectId={pick.projectId}
            projectName={projects.find((p) => p.id === pick.projectId)?.name ?? ''}
            kind={pick.kind}
            questions={questions[pick.kind]}
            onDraft={(seed, evidence, model) => {
              setDraft({ seed, evidence, model });
              setCreateMode('form');
            }}
            onBack={() => setCreateMode('start')}
            onSwitchToForm={() => setCreateMode('form')}
            onCancel={() => setCreating(false)}
            onUnauthorized={onUnauthorized}
          />
        )}
        {creating && projectId !== null && createMode === 'form' && (
          <CreateForm
            defaultCol="issue"
            projects={projects}
            defaultProjectId={pick?.projectId ?? projectId}
            busy={saving}
            members={members}
            labels={labels}
            templates={templates}
            /* **초안에서 온 폼만 잠근다.** 「직접 적기」로 온 폼은 프로젝트·
               유형·상태를 그대로 고를 수 있다 — 그쪽은 앞단계가 없다. */
            locked={draft ? pick : null}
            initial={draft?.seed ?? null}
            note={
              draft ? (
                <>
                  PM 이 세운 초안이다 — 확인하고 고친 뒤 만들어라.
                  {draft.evidence.length > 0 && (
                    <span className="mt-s1 block text-micro">
                      근거: {draft.evidence.map((e) => `“${e}”`).join(' · ')}
                    </span>
                  )}
                </>
              ) : undefined
            }
            onCancel={() => setCreating(false)}
            onSubmit={(input) => void handleCreate(input)}
          />
        )}
      </Modal>

      {/*
        모달에서 고친 것을 보드에 되비춘다. 안 하면 담당자를 바꾸고 닫았을 때
        뒤의 카드가 옛 값을 쥐고 있어서 **저장이 안 된 것처럼 보인다.**
        칸이 바뀌면 개수 배지도 함께 옮긴다.
      */}
      <TicketDialog
        ticketId={openId ?? routeId}
        user={user}
        projects={projects}
        onError={onError}
        onUnauthorized={onUnauthorized}
        onClose={() => {
          setOpenId(null);
          // 주소로 열려 있던 것이면 주소도 보드로 되돌린다 — 안 하면 닫은
          // 뒤 새로고침에 모달이 되살아난다. `replace` 라 뒤로 가기가 닫힌
          // 모달을 다시 열지 않는다. (이때 라우트가 갈려 보드가 한 번 다시
          // 그려진다 — 링크로 들어온 경우뿐이라 받아들였다.)
          if (routeId !== null) void navigate('/', { replace: true });
        }}
        onChanged={(card) => {
          mutationVersion.current++;
          setTickets((prev) => {
            if (!prev) return prev;
            const before = prev.find((t) => t.id === card.id);
            if (before && before.col !== card.col) {
              setCounts((c) =>
                c ? { ...c, [before.col]: c[before.col] - 1, [card.col]: c[card.col] + 1 } : c,
              );
            }
            return prev.map((t) => (t.id === card.id ? card : t));
          });
        }}
        onDeleted={(id) => {
          mutationVersion.current++;
          toast.success('티켓을 지웠다');
          setTickets((prev) => {
            if (!prev) return prev;
            const gone = prev.find((t) => t.id === id);
            if (gone) {
              setCounts((c) => (c ? { ...c, [gone.col]: c[gone.col] - 1 } : c));
            }
            return prev.filter((t) => t.id !== id);
          });
        }}
      />
    </>
  );
}
